import React, { useLayoutEffect, useRef, useState } from "react";
import { Award, ShieldCheck } from "lucide-react";
import {
  CERT_CANVAS,
  CERT_FONT_IMPORT_CSS,
  formatIssueDateForCert,
  getCertificateFontFamily,
  getCertFieldDisplayValue,
  isDynamicCertificateLayout,
  mergeLayoutWithDefaults,
  normalizeLayoutConfig,
} from "../certificateLayoutUtils";

function anchorTransform(align) {
  if (align === "left") return "translate(0, -50%)";
  if (align === "right") return "translate(-100%, -50%)";
  return "translate(-50%, -50%)";
}

function textAlignFor(align) {
  if (align === "left") return "left";
  if (align === "right") return "right";
  return "center";
}

/**
 * Co giữ tỉ lệ theo chiều ngang khung + trần chiều cao viewport.
 * Không gán height cố định trên vỏ ngoài — tránh khoảng trống xám phía dưới.
 */
function CertificateFitScale({ naturalWidth, naturalHeight, children, className = "" }) {
  const boxRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || naturalWidth <= 0 || naturalHeight <= 0) return;

    const measure = () => {
      const rect = box.getBoundingClientRect();
      const padX = 24;
      const availW = Math.max(48, rect.width - padX);
      const vh = typeof window !== "undefined" ? window.innerHeight : 800;
      /** Giới hạn chiều cao phần xem trong modal (đã trừ header + form), không lấy height của khuôn trống */
      const chromeReserve = 280;
      const maxAvailH = Math.max(160, Math.min(vh * 0.62, vh - chromeReserve, 780));

      const scaleW = availW / naturalWidth;
      const scaleH = maxAvailH / naturalHeight;
      const s = Math.min(scaleW, scaleH, 1);
      setScale(Number.isFinite(s) && s > 0 ? s : 1);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [naturalWidth, naturalHeight]);

  const scaledW = naturalWidth * scale;
  const scaledH = naturalHeight * scale;

  return (
    <div
      ref={boxRef}
      className={`w-full max-w-full box-border bg-gray-100 flex justify-center items-start overflow-x-hidden px-3 pt-3 pb-2 ${className}`}
    >
      <div
        className="relative shrink-0 mx-auto"
        style={{
          width: scaledW,
          height: scaledH,
        }}
      >
        <div
          className="absolute top-0 left-1/2"
          style={{
            width: naturalWidth,
            height: naturalHeight,
            transform: `translateX(-50%) scale(${scale})`,
            transformOrigin: "top center",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function DynamicCertificate({ data, layoutConfig, id = "certificate-draw", fitContainer = false }) {
  const layout = mergeLayoutWithDefaults(layoutConfig);
  const w = layout.width || CERT_CANVAS.width;
  const h = layout.height || CERT_CANVAS.height;
  const bg = layout.backgroundUrl;
  const bgFallback = layout.backgroundFallbackDataUrl;

  const canvas = (
    <>
      <style>{CERT_FONT_IMPORT_CSS}</style>
      <div
        id={id}
        className="relative bg-white shadow-2xl overflow-hidden shrink-0"
        style={{
          width: w,
          height: h,
          fontFamily: "'Times New Roman', serif",
        }}
      >
        {bg ? (
          <img
            src={bg}
            alt=""
            className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
            draggable={false}
            onError={(ev) => {
              if (bgFallback && ev.currentTarget.src !== bgFallback) {
                ev.currentTarget.src = bgFallback;
              }
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-white" />
        )}

        {(layout.fields || []).map((f) => {
          const text = getCertFieldDisplayValue(f.key, data, layout);
          const ax = f.x ?? 0.5;
          const ay = f.y ?? 0.5;
          const align = f.align || "center";

          return (
            <div
              key={f.key}
              className="absolute whitespace-pre-wrap break-words max-w-[90%]"
              style={{
                left: `${ax * 100}%`,
                top: `${ay * 100}%`,
                transform: anchorTransform(align),
                color: f.color || "#111827",
                fontSize: f.fontSize ?? 18,
                fontWeight: f.fontWeight || "normal",
                fontFamily: getCertificateFontFamily(f.fontFamily),
                fontStyle: f.fontStyle || "normal",
              textTransform: f.textTransform || "none",
                textAlign: textAlignFor(align),
                lineHeight: 1.2,
                width: f.width ? `${f.width}px` : undefined,
                maxWidth: "90%",
              }}
            >
              {text}
            </div>
          );
        })}
      </div>
    </>
  );

  if (fitContainer) {
    return (
      <CertificateFitScale naturalWidth={w} naturalHeight={h}>
        {canvas}
      </CertificateFitScale>
    );
  }

  return (
    <div className="flex justify-center p-4 bg-gray-100 overflow-x-auto">{canvas}</div>
  );
}

// Component dùng để hiển thị và làm bản vẽ xuất PDF
const CertificateTemplate = ({ data, layoutConfig, id = "certificate-draw", fitContainer = false }) => {
  const normalized = normalizeLayoutConfig(layoutConfig);
  const useDynamic = isDynamicCertificateLayout(normalized);

  if (useDynamic) {
    return (
      <DynamicCertificate data={data} layoutConfig={normalized} id={id} fitContainer={fitContainer} />
    );
  }

  const issueLegacyRaw = formatIssueDateForCert(data?.issue_date);
  const issueLegacy = issueLegacyRaw === "—" ? "15/03/2026" : issueLegacyRaw;

  const legacyW = 842;
  const legacyH = 595;

  const legacyCanvas = (
    <div
      id={id}
      className="relative w-[842px] h-[595px] bg-white shadow-2xl p-12 border-[16px] border-double border-red-700 flex flex-col items-center justify-between text-center"
      style={{ fontFamily: "'Times New Roman', serif" }}
    >
        <div className="absolute top-0 left-0 w-32 h-32 border-t-8 border-l-8 border-red-700/20" />
        <div className="absolute bottom-0 right-0 w-32 h-32 border-b-8 border-r-8 border-red-700/20" />

        <div className="space-y-2">
          <div className="flex justify-center mb-4 text-red-700">
            <Award size={60} strokeWidth={1} />
          </div>
          <h1 className="text-4xl font-black text-gray-800 uppercase tracking-[0.2em]">
            Giấy Chứng Nhận
          </h1>
          <p className="text-xl italic text-gray-500">Trung tâm Giáo dục IMA Việt Nam</p>
        </div>

        <div className="space-y-6">
          <p className="text-lg text-gray-600 uppercase tracking-widest">Chứng nhận cho học viên</p>
          <h2 className="text-5xl font-black text-red-700 uppercase drop-shadow-sm">
            {getCertFieldDisplayValue("student_name", data, null) || "NGUYỄN VĂN A"}
          </h2>
          <div className="max-w-xl mx-auto border-b border-gray-200 pb-2">
            <p className="text-lg text-gray-700 leading-relaxed">
              Đã hoàn thành xuất sắc khóa học{" "}
              <span className="font-bold">{data?.course_name || "Luyện thi Đại học"}</span>
            </p>
          </div>
          <div className="flex justify-center gap-12 text-sm text-gray-500">
            <p>
              Kết quả:{" "}
              <span className="font-bold text-gray-800">{data?.grade || "Hoàn thành khóa học"}</span>
            </p>
            <p>
              Ngày cấp:{" "}
              <span className="font-bold text-gray-800">{issueLegacy}</span>
            </p>
          </div>
        </div>

        <div className="w-full flex justify-around items-end mt-8">
          <div className="text-center">
            <div className="h-16 w-16 mx-auto mb-2 opacity-10">
              <ShieldCheck size={64} />
            </div>
            <p className="text-xs text-gray-400 uppercase tracking-widest">
              Mã số: {data?.cert_id || "IMA-2026-001"}
            </p>
            <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">
              Số vào sổ: {data?.register_no || "SVS-2026-00001"}
            </p>
          </div>
          <div className="text-center">
            <p className="mb-12 font-bold text-gray-800 italic">Giám đốc Trung tâm</p>
            <div className="w-32 h-1 bg-gray-200 mx-auto" />
            <p className="mt-2 text-sm font-bold text-gray-800">IMA VIỆT NAM</p>
          </div>
        </div>
    </div>
  );

  if (fitContainer) {
    return (
      <CertificateFitScale naturalWidth={legacyW} naturalHeight={legacyH}>
        {legacyCanvas}
      </CertificateFitScale>
    );
  }

  return <div className="flex justify-center p-4 bg-gray-100 overflow-x-auto">{legacyCanvas}</div>;
};

export default CertificateTemplate;
